/**
 * EntitlementCard — visual presentation of one entitlement, including
 * progress bar, expiry chip, status badge, and action menu.
 *
 * Pure presentation; no data fetching. Mutations are wired by the parent
 * (StudentDetail) so this component can be re-used in admin lists later.
 */

import React from 'react';
import { Badge, Button, Card, StatusBadge } from '@/components/common';
import { type EntitlementResponse } from '@features/catalog/api/entitlements';
import { useCatalogConfig } from '@features/catalog/api/config';

interface Props {
  entitlement: EntitlementResponse;
  onFreeze?: (e: EntitlementResponse) => void;
  onUnfreeze?: (e: EntitlementResponse) => void;
  onCancel?: (e: EntitlementResponse) => void;
}

function ExpiryChip({
  days,
  urgentDays,
  warningDays,
}: {
  days: number | null | undefined;
  urgentDays: number;
  warningDays: number;
}) {
  if (days === null || days === undefined) {
    return (
      <span data-testid="candeza-catalog-entitlement-expiry-pending">
        <Badge variant="default" size="sm">Activates on first use</Badge>
      </span>
    );
  }
  if (days < 0) {
    return (
      <span data-testid="candeza-catalog-entitlement-expiry-expired">
        <Badge variant="error" size="sm">Expired {Math.abs(days)}d ago</Badge>
      </span>
    );
  }
  if (days < urgentDays) {
    return (
      <span data-testid="candeza-catalog-entitlement-expiry-urgent">
        <Badge variant="error" size="sm">Expires in {days}d</Badge>
      </span>
    );
  }
  if (days < warningDays) {
    return (
      <span data-testid="candeza-catalog-entitlement-expiry-warning">
        <Badge variant="warning" size="sm">Expires in {days}d</Badge>
      </span>
    );
  }
  return (
    <span data-testid="candeza-catalog-entitlement-expiry-ok">
      <Badge variant="default" size="sm">Expires in {days}d</Badge>
    </span>
  );
}

const progressBarStyle: React.CSSProperties = {
  height: 8,
  background: '#e2e8f0',
  borderRadius: 4,
  overflow: 'hidden',
  margin: '8px 0 12px',
};

export const EntitlementCard: React.FC<Props> = ({
  entitlement,
  onFreeze,
  onUnfreeze,
  onCancel,
}) => {
  const { rules } = useCatalogConfig();
  // M1 — prefer server-computed values; fall back to client-side compute only
  // for OpenAPI clients on a stale schema. New backend always provides both.
  const pct =
    typeof entitlement.percent_consumed === 'number'
      ? entitlement.percent_consumed
      : entitlement.sessions_total
        ? Math.round((entitlement.sessions_consumed / entitlement.sessions_total) * 100)
        : 0;
  const daysLeft = entitlement.days_until_expiry;
  const isActive = entitlement.status === 'active';
  const isFrozen = entitlement.status === 'frozen';
  const isCancelled = entitlement.status === 'cancelled';
  const isTerminal = entitlement.status === 'expired' || entitlement.status === 'exhausted' || isCancelled;

  return (
    <Card
      padding="md"
      data-testid={`candeza-catalog-entitlement-row-${entitlement.id}`}
      data-flow="entitlement-row"
      style={{ marginBottom: 12 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <strong data-testid={`candeza-catalog-entitlement-name-${entitlement.id}`}>
              {entitlement.package_snapshot.name}
            </strong>
            <StatusBadge status={entitlement.status} />
            <ExpiryChip
              days={daysLeft}
              urgentDays={rules.entitlement_expiry_urgent_days}
              warningDays={rules.entitlement_expiry_warning_days}
            />
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {entitlement.sessions_consumed} / {entitlement.sessions_total} sessions used
            {' · '}
            {entitlement.sessions_remaining} remaining
          </div>
          <div style={progressBarStyle} aria-label="sessions consumed">
            <div
              data-testid={`candeza-catalog-entitlement-progress-${entitlement.id}`}
              style={{
                width: `${pct}%`,
                height: '100%',
                background: pct >= 100 ? '#94a3b8' : '#6366f1',
                transition: 'width 200ms ease',
              }}
            />
          </div>
          {isCancelled && entitlement.cancellation_reason && (
            <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
              Cancelled: {entitlement.cancellation_reason}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {isActive && onFreeze && (
            <Button
              variant="outline"
              size="sm"
              data-testid={`candeza-catalog-entitlement-freeze-btn-${entitlement.id}`}
              onClick={() => onFreeze(entitlement)}
            >
              Freeze
            </Button>
          )}
          {isFrozen && onUnfreeze && (
            <Button
              variant="outline"
              size="sm"
              data-testid={`candeza-catalog-entitlement-unfreeze-btn-${entitlement.id}`}
              onClick={() => onUnfreeze(entitlement)}
            >
              Unfreeze
            </Button>
          )}
          {!isTerminal && onCancel && (
            <Button
              variant="ghost"
              size="sm"
              data-testid={`candeza-catalog-entitlement-cancel-btn-${entitlement.id}`}
              onClick={() => onCancel(entitlement)}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};


